import { supabase } from "src/lib/supabaseClient";
import { CurriculumImportError, curriculumImportIssue, databaseFailure, explainCurriculumImportFailure } from "src/utils/curriculumImportIssues";

export interface AdminSubject { id: string; name: string }
export interface AdminConcept {
  id: string;
  subject_id: string;
  code: string;
  name: string;
  description: string | null;
  status: "draft" | "published" | "retired";
}
export interface AdminTag { id: string; subject_id: string; code: string; name: string }
export interface AdminObjective {
  id: string;
  concept_id: string;
  code: string;
  outcome: string;
  status: "draft" | "published" | "retired";
}
export interface AdminSubjectObjective extends AdminObjective {
  concept_name: string;
}
export interface AdminPrerequisite {
  prerequisite_objective_id: string;
  rationale: string | null;
}
export interface AdminRubric {
  id: string;
  objective_id: string;
  revision: number;
  status: "draft" | "published" | "retired";
  success_criteria: string[];
  evidence_required: string;
}
export interface AdminAuthority {
  id: string;
  code: string;
  name: string;
  country_code: string;
  region_code: string | null;
}
export interface AdminVersion {
  id: string;
  authority_id: string;
  version_label: string;
  academic_year: string;
  source_reference: string | null;
  status: "draft" | "published" | "retired";
}
export interface AdminPlacement {
  id: string;
  curriculum_version_id: string;
  grade: number;
  objective_id: string;
  rubric_id: string;
  role: "introduce" | "practice" | "assess" | "review";
  requirement: "required" | "optional";
  display_order: number;
}
export interface AdminPathPlacement extends AdminPlacement {
  objective_code: string;
  outcome: string;
  concept_name: string;
  prerequisite_codes: string[];
  order_conflicts: string[];
}
export interface CurriculumBulkImportRow {
  sourceRow: number;
  domainName: string;
  unit: string;
  lesson: string;
  isLessonHeader?: boolean;
  learningObjective: string;
  learningOutcome: string;
  contentStandards: string;
  processStandards: string;
  shortStandard?: string;
  fundamentalKnowledge?: string;
  applications: string;
  connections: string;
  vocabulary: string;
  notes?: string;
  evidenceLabels?: string[];
  concept?: string;
}
export interface CatalogEvidenceTarget {
  id: string;
  kind: "quiz" | "test" | "question";
  label: string;
  targetId: string | null;
  order: number;
}
export interface CatalogNodeEvidence {
  nodeFeedbackActive: boolean;
  nodeFeedbackPrompt: string;
  targets: CatalogEvidenceTarget[];
}
export interface CatalogLesson {
  id: string;
  unitId: string;
  domainId: string;
  name: string;
  order: number;
  status: "draft" | "review" | "published" | "retired";
  instructionalIntent: string;
  contentStandards: string;
  practiceStandards: string;
  shortStandard: string;
  fundamentalKnowledge: string;
  applications: string;
  connections: string;
  vocabulary: string;
  notes: string;
}
export interface ExistingWorkbookImport {
  id: string;
  row_count: number;
  created_at: string;
}
export interface CatalogScope {
  id: string;
  versionLabel: string;
  status: "draft" | "review" | "published" | "retired";
}
export interface CatalogDomain {
  id: string;
  order: number;
  name: string;
  objectiveCount: number;
  units: Array<{ id: string; order: number; name: string; objectiveCount: number; status: "draft" | "review" | "published" | "retired" }>;
  status: "draft" | "review" | "published" | "retired";
}
export interface CatalogObjective {
  id: string;
  order: number;
  unitId: string;
  domainId: string;
  unitName: string;
  lessonId: string;
  lessonName: string;
  statement: string;
  title: string;
  learningObjective: string;
  outcomes: string[];
  criteria: string[];
  standards: string[];
  practiceStandards: string;
  prerequisites: string[];
  status: "draft" | "review" | "published" | "retired";
  updated: string;
  applications: string;
  connections: string;
  vocabulary: string;
  references: string;
}
export class WorkbookAlreadyStagedError extends Error {
  constructor(public readonly batch: ExistingWorkbookImport) {
    super("This workbook was already staged.");
    this.name = "WorkbookAlreadyStagedError";
  }
}

const requireCode = (code: string): string => {
  const value = code.trim().toUpperCase().replace(/\s+/g, "_");
  if (!/^[A-Z0-9_-]+$/.test(value)) throw new Error("Code can contain letters, numbers, underscores, and hyphens.");
  return value;
};

const requireText = (value: string, label: string): string => {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is required.`);
  return trimmed;
};

const parentColumn = (nodeType: "domain" | "unit" | "lesson" | "objective") => `${nodeType}_id`;

const standardCodes = (value: string, pattern: RegExp) => [...new Set(value.match(pattern) || [])];

const replaceLessonStandardIndex = async (lessonId: string, content: string, practice: string) => {
  const { error: removeError } = await supabase.from("curriculum_lesson_standards").delete().eq("lesson_id", lessonId);
  if (removeError) throw databaseFailure(removeError);
  const rows = [
    ...standardCodes(content, /CCSS\.Math\.Content\.[0-9A-Z.]+/g).map((code, display_order) => ({ lesson_id: lessonId, standard_type: "content", code, display_order })),
    ...standardCodes(practice, /SMP\.\d+/g).map((code, display_order) => ({ lesson_id: lessonId, standard_type: "practice", code, display_order })),
  ];
  if (!rows.length) return;
  const { error } = await supabase.from("curriculum_lesson_standards").insert(rows);
  if (error) throw databaseFailure(error);
};

const linkedQuizId = async (label: string): Promise<string | null> => {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(label);
  if (isUuid) {
    const { data, error } = await supabase.from("diagnostic_quizzes").select("id").eq("id", label).maybeSingle();
    if (!error && data?.id) return data.id as string;
  }
  const { data, error } = await supabase.from("diagnostic_quizzes").select("id").eq("title", label).limit(2);
  if (error || data?.length !== 1) return null;
  return data[0].id as string;
};

const ensureAssessmentTargets = async (parent: { lesson_id?: string; objective_id?: string }, labels: string[]) => {
  const cleaned = labels.map((label) => label.trim()).filter(Boolean);
  if (!cleaned.length) return;
  const column = parent.lesson_id ? "lesson_id" : "objective_id";
  const parentId = parent.lesson_id || parent.objective_id;
  if (!parentId) return;
  const { data: existingRequirement, error: requirementError } = await supabase.from("curriculum_evidence_requirements").select("id").eq(column, parentId).eq("kind", "assessment").maybeSingle();
  if (requirementError) throw databaseFailure(requirementError);
  let requirementId = existingRequirement?.id as string | undefined;
  if (!requirementId) {
    const { data, error } = await supabase.from("curriculum_evidence_requirements").insert({ [column]: parentId, kind: "assessment" }).select("id").single();
    if (error) throw databaseFailure(error);
    requirementId = data.id;
  }
  const { data: existingTargets, error: targetError } = await supabase.from("curriculum_evidence_targets").select("label").eq("requirement_id", requirementId);
  if (targetError) throw databaseFailure(targetError);
  const known = new Set((existingTargets || []).map((target) => target.label));
  let order = known.size;
  for (const label of cleaned) {
    if (known.has(label)) continue;
    const targetId = await linkedQuizId(label);
    const { error } = await supabase.from("curriculum_evidence_targets").insert({ requirement_id: requirementId, target_kind: "quiz", target_id: targetId, label, display_order: order });
    if (error) throw databaseFailure(error);
    order += 1;
    known.add(label);
  }
};

const idPages = (ids: string[], size = 80) => {
  const unique = [...new Set(ids.filter(Boolean))];
  const pages: string[][] = [];
  for (let index = 0; index < unique.length; index += size) pages.push(unique.slice(index, index + size));
  return pages;
};

const blockedCatalogDelete = "This record is used by a student plan, a published scope, an exam, or a batch. Remove it there before deleting it.";
const publishedCatalogDelete = "Published curriculum cannot be deleted.";
const publishedVersionMessage = "This curriculum version is published. Create a new version before changing it.";

const assertDraftScope = async (scopeId: string) => {
  const { data, error } = await supabase.from("curriculum_scopes").select("id, status, subject_id").eq("id", scopeId).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Choose a curriculum version first.");
  if (data.status === "published" || data.status === "retired") throw new Error(publishedVersionMessage);
  return data;
};

const scopeIdForCatalogNode = async (kind: "domain" | "unit" | "lesson" | "objective", id: string) => {
  if (kind === "domain") {
    const { data, error } = await supabase.from("curriculum_domains").select("scope_id").eq("id", id).maybeSingle();
    if (error) throw new Error(error.message);
    return data?.scope_id as string | undefined;
  }
  let unitId = kind === "unit" ? id : "";
  if (kind === "lesson") {
    const { data, error } = await supabase.from("curriculum_lessons").select("unit_id").eq("id", id).maybeSingle();
    if (error) throw new Error(error.message);
    unitId = data?.unit_id || "";
  }
  if (kind === "objective") {
    const { data, error } = await supabase.from("curriculum_objectives").select("unit_id").eq("id", id).maybeSingle();
    if (error) throw new Error(error.message);
    unitId = data?.unit_id || "";
  }
  const { data: unit, error: unitError } = await supabase.from("curriculum_units").select("domain_id").eq("id", unitId).maybeSingle();
  if (unitError) throw new Error(unitError.message);
  const { data: domain, error: domainError } = await supabase.from("curriculum_domains").select("scope_id").eq("id", unit?.domain_id || "").maybeSingle();
  if (domainError) throw new Error(domainError.message);
  return domain?.scope_id as string | undefined;
};

const assertCatalogNodeEditable = async (kind: "domain" | "unit" | "lesson" | "objective", id: string) => {
  const scopeId = await scopeIdForCatalogNode(kind, id);
  if (!scopeId) throw new Error("Choose a curriculum version first.");
  await assertDraftScope(scopeId);
};

const countMatching = async (table: string, column: string, ids: string[]): Promise<number> => {
  let total = 0;
  for (const page of idPages(ids)) {
    const { count, error } = await supabase.from(table).select(column, { count: "exact", head: true }).in(column, page);
    if (error) {
      if (error.code === "42P01" || error.code === "PGRST205" || /does not exist|schema cache/i.test(error.message || "")) return total;
      throw new Error(error.message);
    }
    total += count || 0;
  }
  return total;
};

const deleteWhereIn = async (table: string, column: string, ids: string[]) => {
  for (const page of idPages(ids)) {
    const { error } = await supabase.from(table).delete().in(column, page);
    if (error?.code === "23503") throw new Error(blockedCatalogDelete);
    if (error) throw new Error(error.message);
  }
};

const selectWhereIn = async <T,>(table: string, column: string, ids: string[], columns: string): Promise<T[]> => {
  const pages = await Promise.all(idPages(ids).map(async (page) => {
    const { data, error } = await supabase.from(table).select(columns).in(column, page);
    if (error) throw new Error(error.message);
    return (data || []) as T[];
  }));
  return pages.flat();
};

const sameLessonOutcomeIds = async (id: string): Promise<string[]> => {
  const [current] = await selectWhereIn<{ id: string; lesson_id: string | null; statement: string | null; status: string }>("curriculum_objectives", "id", [id], "id, lesson_id, statement, status");
  const statement = (current?.statement || "").trim();
  if (!current?.lesson_id || !statement) return [id];
  const siblings = await selectWhereIn<{ id: string; statement: string | null; status: string }>("curriculum_objectives", "lesson_id", [current.lesson_id], "id, statement, status");
  const candidates = siblings.filter((row) => row.status !== "published" && row.status !== "retired" && (row.statement || "").trim() === statement).map((row) => row.id);
  if (!candidates.includes(id)) return [id];
  const links = await selectWhereIn<{ scope_id: string; objective_id: string }>("curriculum_scope_objectives", "objective_id", candidates, "scope_id, objective_id");
  if (!links.length) return candidates;
  const scopes = await selectWhereIn<{ id: string; status: string }>("curriculum_scopes", "id", links.map((link) => link.scope_id), "id, status");
  const locked = new Set(scopes.filter((scope) => scope.status === "published" || scope.status === "retired").map((scope) => scope.id));
  const editable = candidates.filter((objectiveId) => !links.some((link) => link.objective_id === objectiveId && locked.has(link.scope_id)));
  return editable.includes(id) ? editable : [id];
};

const loadNodeEvidence = async (nodeIds: string[]): Promise<Record<string, CatalogNodeEvidence>> => {
  const ids = [...new Set(nodeIds.filter(Boolean))];
  if (!ids.length) return {};
  const requirementPages = await Promise.all(idPages(ids).map(async (page) => {
    const filter = page.join(",");
    const { data, error } = await supabase.from("curriculum_evidence_requirements").select("id, domain_id, unit_id, lesson_id, objective_id, kind, prompt, active").or(`domain_id.in.(${filter}),unit_id.in.(${filter}),lesson_id.in.(${filter}),objective_id.in.(${filter})`);
    if (error) throw new Error(error.message);
    return data || [];
  }));
  const requirements = requirementPages.flat();
  const requirementIds = requirements.map((requirement) => requirement.id);
  const targets = requirementIds.length
    ? await selectWhereIn<{ id: string; requirement_id: string; target_kind: "quiz" | "test" | "question"; target_id: string | null; label: string; display_order: number }>("curriculum_evidence_targets", "requirement_id", requirementIds, "id, requirement_id, target_kind, target_id, label, display_order")
    : [];
  const evidence: Record<string, CatalogNodeEvidence> = {};
  for (const requirement of requirements || []) {
    const nodeId = requirement.domain_id || requirement.unit_id || requirement.lesson_id || requirement.objective_id;
    const current = evidence[nodeId] || { nodeFeedbackActive: false, nodeFeedbackPrompt: "", targets: [] };
    if (requirement.kind === "node_feedback") {
      current.nodeFeedbackActive = requirement.active;
      current.nodeFeedbackPrompt = requirement.prompt || "";
    } else {
      current.targets = (targets || []).filter((target) => target.requirement_id === requirement.id).map((target) => ({ id: target.id, kind: target.target_kind, label: target.label, targetId: target.target_id, order: target.display_order }));
    }
    evidence[nodeId] = current;
  }
  return evidence;
};

export const adminCurriculumService = {
  async saveCatalogObjective(objective: CatalogObjective): Promise<string> {
    await assertCatalogNodeEditable("objective", objective.id);
    const now = new Date().toISOString();
    const statement = requireText(objective.statement || objective.title, "Objective");
    const { error: objectiveError } = await supabase.from("curriculum_objectives").update({
      unit_id: objective.unitId,
      lesson_id: objective.lessonId || null,
      title: statement,
      statement,
      learning_outcomes: [statement],
      display_order: objective.order,
      status: objective.status,
      updated_at: now,
    }).eq("id", objective.id);
    if (objectiveError) throw new Error(objectiveError.message);
    const { error: removePrerequisitesError } = await supabase.from("curriculum_objective_prerequisites").delete().eq("objective_id", objective.id);
    if (removePrerequisitesError) throw new Error(removePrerequisitesError.message);
    if (objective.prerequisites.length) {
      const { error } = await supabase.from("curriculum_objective_prerequisites").insert(objective.prerequisites.map((prerequisite_objective_id, display_order) => ({ objective_id: objective.id, prerequisite_objective_id, display_order })));
      if (error) throw new Error(error.message);
    }
    return now;
  },
  async saveCatalogLesson(lesson: CatalogLesson): Promise<void> {
    await assertCatalogNodeEditable("lesson", lesson.id);
    const { error } = await supabase.from("curriculum_lessons").update({
      name: requireText(lesson.name, "Lesson name"),
      instructional_intent: lesson.instructionalIntent,
      content_standards: lesson.contentStandards,
      practice_standards: lesson.practiceStandards,
      short_standard: lesson.shortStandard,
      fundamental_knowledge: lesson.fundamentalKnowledge,
      applications: lesson.applications,
      connections: lesson.connections,
      vocabulary: lesson.vocabulary,
      notes: lesson.notes,
      display_order: lesson.order,
      updated_at: new Date().toISOString(),
    }).eq("id", lesson.id);
    if (error) throw new Error(error.message);
    await replaceLessonStandardIndex(lesson.id, lesson.contentStandards, lesson.practiceStandards);
  },
  async publishCatalogScopeVersion(input: {
    subjectId: string;
    boardId: string;
    gradeId: string;
    versionLabel: string;
    objectiveIds: string[];
  }): Promise<string> {
    if (!input.objectiveIds.length) throw new Error("Add at least one objective before publishing.");
    const { data, error } = await supabase.rpc("publish_curriculum_scope_version", {
      p_subject_id: input.subjectId,
      p_board_id: input.boardId,
      p_grade_id: input.gradeId,
      p_version_label: input.versionLabel,
      p_objective_ids: input.objectiveIds,
    });
    if (error) {
      const diagnostic = [error.message, error.details, error.hint].filter(Boolean).join(" ");
      console.error("Unable to publish curriculum scope version", error);
      throw new Error(diagnostic || "Unable to publish the curriculum scope version.");
    }
    if (!data) throw new Error("The curriculum scope was not created.");
    return data as string;
  },
  async listCatalogDomains(subjectId: string): Promise<{ domains: CatalogDomain[]; lessons: CatalogLesson[] }> {
    const { data: domains, error: domainsError } = await supabase.from("curriculum_domains").select("id, name, display_order, status").eq("subject_id", subjectId).order("display_order");
    if (domainsError) throw new Error(domainsError.message);
    const domainIds = (domains || []).map((domain) => domain.id);
    if (!domainIds.length) return { domains: [], lessons: [] };
    const { data: units, error: unitsError } = await supabase.from("curriculum_units").select("id, domain_id, name, display_order, status").in("domain_id", domainIds).order("display_order");
    if (unitsError) throw new Error(unitsError.message);
    const unitIds = (units || []).map((unit) => unit.id);
    const { data: lessons, error: lessonsError } = unitIds.length
      ? await supabase.from("curriculum_lessons").select("id, unit_id, name, display_order, status").in("unit_id", unitIds).order("display_order")
      : { data: [], error: null };
    if (lessonsError) throw new Error(lessonsError.message);
    const unitDomain = new Map((units || []).map((unit) => [unit.id, unit.domain_id]));
    return {
      domains: (domains || []).map((domain) => ({
        id: domain.id, name: domain.name, order: domain.display_order, status: domain.status, objectiveCount: 0,
        units: (units || []).filter((unit) => unit.domain_id === domain.id).map((unit) => ({ id: unit.id, name: unit.name, order: unit.display_order, status: unit.status, objectiveCount: 0 })),
      })),
      lessons: (lessons || []).map((lesson) => ({
        id: lesson.id, unitId: lesson.unit_id, domainId: unitDomain.get(lesson.unit_id) || "", name: lesson.name, order: lesson.display_order, status: lesson.status,
        instructionalIntent: "", contentStandards: "", practiceStandards: "", shortStandard: "", fundamentalKnowledge: "", applications: "", connections: "", vocabulary: "", notes: "",
      })),
    };
  },
  async listCatalogScopes(input: { subjectId: string; boardId: string; gradeId: string }): Promise<CatalogScope[]> {
    const { data, error } = await supabase.from("curriculum_scopes").select("id, version_label, status, created_at").eq("subject_id", input.subjectId).eq("board_id", input.boardId).eq("grade_id", input.gradeId).order("created_at");
    if (error) throw new Error(error.message);
    return (data || []).map((scope) => ({ id: scope.id, versionLabel: scope.version_label, status: scope.status }));
  },
  async copyCatalogScope(sourceScopeId: string, versionLabel: string): Promise<CatalogScope> {
    const label = versionLabel.trim().replace(/–/g, "-");
    if (!label) throw new Error("Enter a name for the new version.");
    const { data, error } = await supabase.rpc("copy_curriculum_scope_version", { p_source_scope_id: sourceScopeId, p_version_label: label });
    if (error) throw new Error(error.message);
    if (!data) throw new Error("The new curriculum version was not created.");
    return { id: data as string, versionLabel: label, status: "draft" };
  },
  async listScopeCurriculum(scopeId: string): Promise<{ domains: CatalogDomain[]; lessons: CatalogLesson[]; objectives: CatalogObjective[]; evidence: Record<string, CatalogNodeEvidence> }> {
    const { data: domains, error: domainsError } = await supabase.from("curriculum_domains").select("id, name, display_order, status").eq("scope_id", scopeId).order("display_order");
    if (domainsError) throw new Error(domainsError.message);
    const domainIds = (domains || []).map((domain) => domain.id);
    if (!domainIds.length) return { domains: [], lessons: [], objectives: [], evidence: {} };
    const units = await selectWhereIn<{ id: string; domain_id: string; name: string; display_order: number; status: CatalogDomain["status"] }>("curriculum_units", "domain_id", domainIds, "id, domain_id, name, display_order, status");
    const unitIds = units.map((unit) => unit.id);
    const lessons = unitIds.length ? await selectWhereIn<Record<string, any>>("curriculum_lessons", "unit_id", unitIds, "id, unit_id, name, display_order, status, instructional_intent, content_standards, practice_standards, short_standard, fundamental_knowledge, applications, connections, vocabulary, notes") : [];
    const objectives = unitIds.length ? await selectWhereIn<Record<string, any>>("curriculum_objectives", "unit_id", unitIds, "id, unit_id, lesson_id, title, statement, learning_objective, learning_outcomes, success_criteria, applications, connections, vocabulary, display_order, status, updated_at") : [];
    const objectiveIds = objectives.map((objective) => objective.id as string);
    const [standards, prerequisites] = await Promise.all([
      selectWhereIn<{ objective_id: string; standard_type: string; code: string }>("curriculum_objective_standards", "objective_id", objectiveIds, "objective_id, standard_type, code, display_order"),
      selectWhereIn<{ objective_id: string; prerequisite_objective_id: string }>("curriculum_objective_prerequisites", "objective_id", objectiveIds, "objective_id, prerequisite_objective_id"),
    ]);
    const unitById = new Map(units.map((unit) => [unit.id, unit]));
    const lessonById = new Map(lessons.map((lesson) => [lesson.id as string, lesson]));
    const standardsByObjective = new Map<string, Array<{ standard_type: string; code: string }>>();
    standards.forEach((standard) => standardsByObjective.set(standard.objective_id, [...(standardsByObjective.get(standard.objective_id) || []), standard]));
    const prerequisitesByObjective = new Map<string, string[]>();
    prerequisites.forEach((item) => prerequisitesByObjective.set(item.objective_id, [...(prerequisitesByObjective.get(item.objective_id) || []), item.prerequisite_objective_id]));
    const nodeIds = [...domainIds, ...unitIds, ...lessons.map((lesson) => lesson.id as string), ...objectiveIds];
    const evidence = await loadNodeEvidence(nodeIds);
    return {
      evidence,
      domains: (domains || []).map((domain) => ({
        id: domain.id, name: domain.name, order: domain.display_order, status: domain.status, objectiveCount: 0,
        units: units.filter((unit) => unit.domain_id === domain.id).sort((a, b) => a.display_order - b.display_order).map((unit) => ({ id: unit.id, name: unit.name, order: unit.display_order, status: unit.status, objectiveCount: 0 })),
      })),
      lessons: lessons.map((lesson) => ({
        id: lesson.id, unitId: lesson.unit_id, domainId: unitById.get(lesson.unit_id)?.domain_id || "", name: lesson.name, order: lesson.display_order, status: lesson.status || "draft",
        instructionalIntent: lesson.instructional_intent || "", contentStandards: lesson.content_standards || "", practiceStandards: lesson.practice_standards || "", shortStandard: lesson.short_standard || "", fundamentalKnowledge: lesson.fundamental_knowledge || "", applications: lesson.applications || "", connections: lesson.connections || "", vocabulary: lesson.vocabulary || "", notes: lesson.notes || "",
      })).sort((a, b) => a.order - b.order),
      objectives: objectives.map((objective) => {
        const unit = unitById.get(objective.unit_id);
        const lesson = lessonById.get(objective.lesson_id);
        const objectiveStandards = standardsByObjective.get(objective.id) || [];
        const statement = objective.statement || (objective.learning_outcomes || [])[0] || "";
        return {
          id: objective.id, order: objective.display_order, unitId: objective.unit_id, lessonId: objective.lesson_id || "", lessonName: lesson?.name || "", domainId: unit?.domain_id || "", unitName: unit?.name || "Unclassified unit",
          statement, title: statement || objective.title, learningObjective: lesson?.instructional_intent || objective.learning_objective, outcomes: statement ? [statement] : (objective.learning_outcomes || []), criteria: objective.success_criteria || [],
          standards: lesson?.short_standard ? [lesson.short_standard] : objectiveStandards.filter((item) => item.standard_type === "content").map((item) => item.code),
          practiceStandards: lesson?.practice_standards || objectiveStandards.filter((item) => item.standard_type === "practice").map((item) => item.code).join("\n"),
          prerequisites: prerequisitesByObjective.get(objective.id) || [], status: objective.status, updated: objective.updated_at, applications: lesson?.applications || objective.applications, connections: lesson?.connections || objective.connections, vocabulary: lesson?.vocabulary || objective.vocabulary, references: "",
        };
      }).sort((a, b) => a.order - b.order),
    };
  },
  async createCatalogDomain(subjectId: string, scopeId: string, name: string, displayOrder: number): Promise<CatalogDomain> {
    const scope = await assertDraftScope(scopeId);
    if (scope.subject_id !== subjectId) throw new Error("Choose a curriculum version for this subject.");
    const { data, error } = await supabase.from("curriculum_domains").insert({ subject_id: subjectId, scope_id: scopeId, name: requireText(name, "Domain name"), display_order: displayOrder }).select("id, name, display_order").single();
    if (error) throw new Error(error.message);
    return { id: data.id, name: data.name, order: data.display_order, status: "draft", objectiveCount: 0, units: [] };
  },
  async createCatalogUnit(domainId: string, name: string, displayOrder: number): Promise<{ id: string; name: string; order: number; status: CatalogDomain["status"]; objectiveCount: number }> {
    await assertCatalogNodeEditable("domain", domainId);
    const { data, error } = await supabase.from("curriculum_units").insert({ domain_id: domainId, name: requireText(name, "Unit name"), display_order: displayOrder }).select("id, name, display_order").single();
    if (error) throw new Error(error.message);
    return { id: data.id, name: data.name, order: data.display_order, status: "draft", objectiveCount: 0 };
  },
  async createCatalogLesson(unitId: string, domainId: string, name: string, displayOrder: number): Promise<CatalogLesson> {
    await assertCatalogNodeEditable("unit", unitId);
    const lessonName = requireText(name, "Lesson name");
    const { data, error } = await supabase.from("curriculum_lessons").insert({ unit_id: unitId, name: lessonName, display_order: displayOrder }).select("id, name, display_order").single();
    if (error) throw new Error(error.message);
    return { id: data.id, unitId, domainId, name: data.name, order: data.display_order, status: "draft", instructionalIntent: "", contentStandards: "", practiceStandards: "", shortStandard: "", fundamentalKnowledge: "", applications: "", connections: "", vocabulary: "", notes: "" };
  },
  async deleteCatalogNode(kind: "domain" | "unit" | "lesson" | "objective", id: string): Promise<{ importRowsRemoved: number }> {
    await assertCatalogNodeEditable(kind, id);
    const unitIds = kind === "domain"
      ? (await selectWhereIn<{ id: string }>("curriculum_units", "domain_id", [id], "id")).map((row) => row.id)
      : kind === "unit" ? [id] : [];
    const lessonIds = kind === "lesson"
      ? [id]
      : (await selectWhereIn<{ id: string }>("curriculum_lessons", "unit_id", unitIds, "id")).map((row) => row.id);
    const objectiveIds = kind === "objective"
      ? await sameLessonOutcomeIds(id)
      : lessonIds.length
        ? (await selectWhereIn<{ id: string }>("curriculum_objectives", "lesson_id", lessonIds, "id")).map((row) => row.id)
        : (await selectWhereIn<{ id: string }>("curriculum_objectives", "unit_id", unitIds, "id")).map((row) => row.id);
    const domainIds = kind === "domain" ? [id] : [];
    const includesPublished = async (table: string, ids: string[]) => (await selectWhereIn<{ status: string }>(table, "id", ids, "id, status")).some((row) => row.status === "published");
    if (
      await includesPublished("curriculum_domains", domainIds)
      || await includesPublished("curriculum_units", unitIds)
      || await includesPublished("curriculum_lessons", lessonIds)
      || await includesPublished("curriculum_objectives", objectiveIds)
    ) throw new Error(publishedCatalogDelete);
    const blockers = [
      ["subscription_curriculum_objectives", "objective_id", objectiveIds],
      ["subscription_curriculum_prerequisite_waivers", "objective_id", objectiveIds],
      ["subscription_curriculum_prerequisite_waivers", "prerequisite_objective_id", objectiveIds],
      ["subscription_curriculum_selection_targets", "objective_id", objectiveIds],
      ["subscription_curriculum_selection_targets", "lesson_id", lessonIds],
      ["subscription_curriculum_selection_targets", "unit_id", unitIds],
      ["subscription_curriculum_selection_targets", "domain_id", domainIds],
      ["exam_blueprint_objectives", "objective_id", objectiveIds],
      ["batch_curriculum_objectives", "objective_id", objectiveIds],
      ["batch_curriculum_units", "unit_id", unitIds],
    ] as const;
    for (const [table, column, ids] of blockers) {
      if (await countMatching(table, column, [...ids])) throw new Error(blockedCatalogDelete);
    }
    const scopeLinks = await selectWhereIn<{ scope_id: string }>("curriculum_scope_objectives", "objective_id", objectiveIds, "scope_id, objective_id");
    if (scopeLinks.length) {
      const scopes = await selectWhereIn<{ id: string; status: string }>("curriculum_scopes", "id", scopeLinks.map((link) => link.scope_id), "id, status");
      const lockedScope = new Set(scopes.filter((scope) => scope.status === "published" || scope.status === "retired").map((scope) => scope.id));
      if (scopeLinks.some((link) => lockedScope.has(link.scope_id))) throw new Error(blockedCatalogDelete);
      await deleteWhereIn("curriculum_scope_objectives", "objective_id", objectiveIds);
    }
    let importRowsRemoved = 0;
    const removeImportRows = async (column: string, ids: string[]) => {
      for (const page of idPages(ids)) {
        const { data, error } = await supabase.from("curriculum_import_rows").delete().in(column, page).select("id");
        if (error) throw new Error(error.message);
        importRowsRemoved += data?.length || 0;
      }
    };
    await removeImportRows("objective_id", objectiveIds);
    await removeImportRows("lesson_id", lessonIds);
    await removeImportRows("unit_id", unitIds);
    await removeImportRows("domain_id", domainIds);
    await deleteWhereIn("curriculum_objective_prerequisites", "prerequisite_objective_id", objectiveIds);
    await deleteWhereIn("curriculum_objective_prerequisites", "objective_id", objectiveIds);
    for (const page of idPages(objectiveIds)) {
      const { data, error } = await supabase.from("curriculum_objectives").delete().in("id", page).select("id");
      if (error?.code === "23503") throw new Error(blockedCatalogDelete);
      if (error) throw new Error(error.message);
      const removed = new Set((data || []).map((row) => row.id as string));
      if (page.some((objectiveId) => !removed.has(objectiveId))) throw new Error("That objective could not be deleted. Refresh and try again.");
    }
    if (kind !== "objective") {
      const table = kind === "domain" ? "curriculum_domains" : kind === "unit" ? "curriculum_units" : "curriculum_lessons";
      const { error } = await supabase.from(table).delete().eq("id", id);
      if (error?.code === "23503") throw new Error(blockedCatalogDelete);
      if (error) throw new Error(error.message);
    }
    return { importRowsRemoved };
  },
  async listStagedCurriculum(input: { subjectId: string; boardId?: string; gradeId?: string; scopeId?: string; importsOnly?: boolean }): Promise<{ objectives: CatalogObjective[]; lessons: CatalogLesson[]; evidence: Record<string, CatalogNodeEvidence>; imports: Array<{ id: string; fileName: string; rowCount: number; expectedCount: number }> }> {
    let batchesQuery = supabase.from("curriculum_import_batches").select("id, file_name, row_count, created_at").eq("subject_id", input.subjectId).order("created_at", { ascending: false });
    if (input.scopeId) batchesQuery = batchesQuery.eq("scope_id", input.scopeId);
    batchesQuery = input.boardId ? batchesQuery.eq("board_id", input.boardId) : batchesQuery.is("board_id", null);
    batchesQuery = input.gradeId ? batchesQuery.eq("grade_id", input.gradeId) : batchesQuery.is("grade_id", null);
    const { data: batches, error: batchesError } = await batchesQuery;
    if (batchesError) throw new Error(batchesError.message);
    const batchIds = (batches || []).map((batch) => batch.id);
    if (!batchIds.length) return { objectives: [], lessons: [], evidence: {}, imports: [] };
    const loadedRows = await selectWhereIn<{ batch_id: string; source_row: number; domain_id: string | null; unit_id: string | null; lesson_id: string | null; objective_id: string | null }>("curriculum_import_rows", "batch_id", batchIds, "batch_id, source_row, domain_id, unit_id, lesson_id, objective_id");
    const rowCountByBatch = new Map<string, number>();
    loadedRows.forEach((row) => rowCountByBatch.set(row.batch_id, (rowCountByBatch.get(row.batch_id) || 0) + 1));
    const keptBatchIds = new Set<string>();
    const batchesByFile = new Map<string, typeof batches>();
    (batches || []).forEach((batch) => {
      const key = batch.file_name;
      batchesByFile.set(key, [...(batchesByFile.get(key) || []), batch]);
    });
    const extraBatchIds: string[] = [];
    batchesByFile.forEach((group) => {
      const [kept, ...extras] = [...group].sort((a, b) => (rowCountByBatch.get(b.id) || 0) - (rowCountByBatch.get(a.id) || 0) || a.created_at.localeCompare(b.created_at));
      if (kept && (rowCountByBatch.get(kept.id) || 0) > 0) keptBatchIds.add(kept.id);
      extras.forEach((batch) => extraBatchIds.push(batch.id));
    });
    if (extraBatchIds.length) {
      const keptObjectiveIds = new Set(loadedRows.filter((row) => keptBatchIds.has(row.batch_id) && row.objective_id).map((row) => row.objective_id as string));
      const orphanObjectiveIds = [...new Set(loadedRows.filter((row) => extraBatchIds.includes(row.batch_id) && row.objective_id && !keptObjectiveIds.has(row.objective_id)).map((row) => row.objective_id as string))];
      await supabase.from("curriculum_import_batches").delete().in("id", extraBatchIds);
      if (orphanObjectiveIds.length) await supabase.from("curriculum_objectives").delete().in("id", orphanObjectiveIds);
    }
    const rows = loadedRows.filter((row) => keptBatchIds.has(row.batch_id));
    const imports = (batches || []).flatMap((batch) => {
      if (!keptBatchIds.has(batch.id)) return [];
      const rowCount = rowCountByBatch.get(batch.id) || 0;
      return rowCount ? [{ id: batch.id, fileName: batch.file_name, rowCount, expectedCount: batch.row_count }] : [];
    });
    if (input.importsOnly) return { objectives: [], lessons: [], evidence: {}, imports };
    const objectiveIds = rows.map((row) => row.objective_id).filter((id): id is string => Boolean(id));
    if (!objectiveIds.length) return { objectives: [], lessons: [], evidence: {}, imports };
    const lessonIds = [...new Set(rows.map((row) => row.lesson_id).filter((id): id is string => Boolean(id)))];
    const [objectives, standards, prerequisites, units, domains, lessons] = await Promise.all([
      selectWhereIn<Record<string, any>>("curriculum_objectives", "id", objectiveIds, "id, unit_id, lesson_id, title, statement, learning_objective, learning_outcomes, success_criteria, applications, connections, vocabulary, display_order, status, updated_at"),
      selectWhereIn<{ objective_id: string; standard_type: string; code: string }>("curriculum_objective_standards", "objective_id", objectiveIds, "objective_id, standard_type, code, display_order"),
      selectWhereIn<{ objective_id: string; prerequisite_objective_id: string }>("curriculum_objective_prerequisites", "objective_id", objectiveIds, "objective_id, prerequisite_objective_id"),
      selectWhereIn<{ id: string; domain_id: string; name: string }>("curriculum_units", "id", rows.map((row) => row.unit_id || ""), "id, domain_id, name"),
      selectWhereIn<{ id: string; name: string }>("curriculum_domains", "id", rows.map((row) => row.domain_id || ""), "id, name"),
      lessonIds.length ? selectWhereIn<Record<string, any>>("curriculum_lessons", "id", lessonIds, "id, unit_id, name, display_order, status, instructional_intent, content_standards, practice_standards, short_standard, fundamental_knowledge, applications, connections, vocabulary, notes") : Promise.resolve([]),
    ]);
    const rowByObjectiveId = new Map(rows.map((row) => [row.objective_id, row]));
    const unitById = new Map((units || []).map((unit) => [unit.id, unit]));
    const domainById = new Map((domains || []).map((domain) => [domain.id, domain]));
    const standardsByObjective = new Map<string, Array<{ standard_type: string; code: string }>>();
    (standards || []).forEach((standard) => standardsByObjective.set(standard.objective_id, [...(standardsByObjective.get(standard.objective_id) || []), standard]));
    const prerequisitesByObjective = new Map<string, string[]>();
    (prerequisites || []).forEach((item) => prerequisitesByObjective.set(item.objective_id, [...(prerequisitesByObjective.get(item.objective_id) || []), item.prerequisite_objective_id]));
    const lessonById = new Map((lessons || []).map((lesson) => [lesson.id, lesson]));
    const nodeIds = [...domainById.keys(), ...unitById.keys(), ...lessonById.keys(), ...(objectives || []).map((objective) => objective.id)];
    const evidence = await loadNodeEvidence(nodeIds);
    return {
      imports,
      evidence,
      lessons: (lessons || []).map((lesson) => {
        const unit = unitById.get(lesson.unit_id);
        return { id: lesson.id, unitId: lesson.unit_id, domainId: unit?.domain_id || "", name: lesson.name, order: lesson.display_order, status: lesson.status || "draft", instructionalIntent: lesson.instructional_intent || "", contentStandards: lesson.content_standards || "", practiceStandards: lesson.practice_standards || "", shortStandard: lesson.short_standard || "", fundamentalKnowledge: lesson.fundamental_knowledge || "", applications: lesson.applications || "", connections: lesson.connections || "", vocabulary: lesson.vocabulary || "", notes: lesson.notes || "" };
      }).sort((a, b) => a.order - b.order),
      objectives: (objectives || []).map((objective) => {
        const row = rowByObjectiveId.get(objective.id); const unit = unitById.get(objective.unit_id); const lesson = lessonById.get(objective.lesson_id); const objectiveStandards = standardsByObjective.get(objective.id) || [];
        const statement = objective.statement || (objective.learning_outcomes || [])[0] || "";
        return { id: objective.id, order: objective.display_order, unitId: objective.unit_id, lessonId: objective.lesson_id || "", lessonName: lesson?.name || "", domainId: unit?.domain_id || row?.domain_id || "", unitName: unit?.name || "Unclassified unit", statement, title: statement || objective.title, learningObjective: lesson?.instructional_intent || objective.learning_objective, outcomes: statement ? [statement] : (objective.learning_outcomes || []), criteria: objective.success_criteria || [], standards: lesson?.short_standard ? [lesson.short_standard] : objectiveStandards.filter((item) => item.standard_type === "content").map((item) => item.code), practiceStandards: lesson?.practice_standards || objectiveStandards.filter((item) => item.standard_type === "practice").map((item) => item.code).join("\n"), prerequisites: prerequisitesByObjective.get(objective.id) || [], status: objective.status, updated: objective.updated_at, applications: lesson?.applications || objective.applications, connections: lesson?.connections || objective.connections, vocabulary: lesson?.vocabulary || objective.vocabulary, references: row ? `Imported row ${row.source_row}` : "" };
      }).sort((a, b) => a.order - b.order),
    };
  },
  async findWorkbookImport(input: { subjectId: string; boardId?: string; gradeId?: string; scopeId: string; fileName: string; sheetName: string; contentHash: string }): Promise<ExistingWorkbookImport | null> {
    let query = supabase.from("curriculum_import_batches").select("id, row_count, created_at").eq("subject_id", input.subjectId).eq("scope_id", input.scopeId).eq("file_name", input.fileName).eq("sheet_name", input.sheetName).order("created_at", { ascending: true }).limit(1);
    query = input.boardId ? query.eq("board_id", input.boardId) : query.is("board_id", null);
    query = input.gradeId ? query.eq("grade_id", input.gradeId) : query.is("grade_id", null);
    const { data, error } = await query.maybeSingle();
    if (error) throw databaseFailure(error);
    return data as ExistingWorkbookImport | null;
  },
  async countLinkedImportRows(batchId: string): Promise<number> {
    const { count, error } = await supabase.from("curriculum_import_rows").select("id", { count: "exact", head: true }).eq("batch_id", batchId).not("objective_id", "is", null);
    if (error) throw databaseFailure(error);
    return count || 0;
  },
  async stageWorkbookImport(input: { subjectId: string; boardId?: string; gradeId?: string; scopeId: string; fileName: string; sheetName: string; contentHash: string; allowReimport?: boolean; rows: CurriculumBulkImportRow[] }, onProgress?: (completed: number) => void): Promise<{ batchId: string }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new CurriculumImportError(curriculumImportIssue("stage.signed_out", { phase: "stage", expectedCount: input.rows.length, savedCount: 0 }));
    let completed = 0;
    try {
    if (!input.scopeId) throw new CurriculumImportError(curriculumImportIssue("stage.version_required", { phase: "stage", expectedCount: input.rows.length, savedCount: 0, message: "Choose a draft curriculum version before staging this workbook." }));
    const scope = await assertDraftScope(input.scopeId).catch((error: unknown) => {
      if (error instanceof Error && error.message === publishedVersionMessage) {
        throw new CurriculumImportError(curriculumImportIssue("stage.published_version", { phase: "stage", expectedCount: input.rows.length, savedCount: 0, message: "This curriculum version is published. Create a new version before staging into it." }));
      }
      throw error;
    });
    if (scope.subject_id !== input.subjectId) throw new CurriculumImportError(curriculumImportIssue("stage.version_required", { phase: "stage", expectedCount: input.rows.length, savedCount: 0, message: "Choose a draft curriculum version before staging this workbook." }));
    const doneSources = new Set<number>();
    let batchId = "";
    const existing = await this.findWorkbookImport(input);
    if (existing) {
      const { data: stagedRows, error: stagedError } = await supabase.from("curriculum_import_rows").select("id, source_row, objective_id").eq("batch_id", existing.id);
      if (stagedError) throw databaseFailure(stagedError);
      const linked = (stagedRows || []).filter((row) => row.objective_id);
      if (linked.length >= input.rows.length) throw new WorkbookAlreadyStagedError({ ...existing, row_count: linked.length });
      linked.forEach((row) => doneSources.add(row.source_row as number));
      completed = doneSources.size;
      const danglingIds = (stagedRows || []).filter((row) => !row.objective_id).map((row) => row.id as string);
      if (danglingIds.length) {
        const { error: deleteError } = await supabase.from("curriculum_import_rows").delete().in("id", danglingIds);
        if (deleteError) throw databaseFailure(deleteError);
      }
      batchId = existing.id;
    }
    if (!batchId) {
      const { data: batch, error: batchError } = await supabase.from("curriculum_import_batches").insert({ subject_id: input.subjectId, board_id: input.boardId || null, grade_id: input.gradeId || null, scope_id: input.scopeId, file_name: input.fileName, sheet_name: input.sheetName, content_hash: input.contentHash, is_reimport: false, row_count: input.rows.length, created_by: user.id }).select("id").single();
      if (batchError) {
        if (batchError.code === "23505" && !input.allowReimport) {
          const existing = await this.findWorkbookImport(input);
          if (existing) throw new WorkbookAlreadyStagedError(existing);
        }
        throw databaseFailure(batchError);
      }
      batchId = batch.id;
    }
    const domains = new Map<string, string>(); const units = new Map<string, string>(); const lessons = new Map<string, string>();
    let objectiveOrder = doneSources.size;
    completed = doneSources.size;
    for (const row of input.rows) {
      if (doneSources.has(row.sourceRow)) { onProgress?.(input.rows.indexOf(row) + 1); continue; }
      try {
      const domainName = requireText(row.domainName, "Domain");
      let domainId = domains.get(domainName);
      if (!domainId) { const { data, error } = await supabase.from("curriculum_domains").upsert({ subject_id: input.subjectId, scope_id: input.scopeId, name: domainName }, { onConflict: "subject_id,scope_id,name" }).select("id").single(); if (error) throw databaseFailure(error); domainId = data.id; domains.set(domainName, domainId); }
      const unitKey = `${domainId}:${row.unit}`; let unitId = units.get(unitKey);
      if (!unitId) { const { data, error } = await supabase.from("curriculum_units").upsert({ domain_id: domainId, name: requireText(row.unit, "Unit") }, { onConflict: "domain_id,name" }).select("id").single(); if (error) throw databaseFailure(error); unitId = data.id; units.set(unitKey, unitId); }
      const lessonName = requireText(row.lesson, "Lesson"); const lessonKey = `${unitId}:${lessonName}`; let lessonId = lessons.get(lessonKey);
      const isHeader = row.isLessonHeader !== false && !lessonId;
      if (!lessonId) {
        const lessonFields = isHeader ? { instructional_intent: row.learningObjective || "", content_standards: row.contentStandards || "", practice_standards: row.processStandards || "", short_standard: row.shortStandard || "", fundamental_knowledge: row.fundamentalKnowledge || "", applications: row.applications || "", connections: row.connections || "", vocabulary: row.vocabulary || "", notes: row.notes || "" } : {};
        const { data, error } = await supabase.from("curriculum_lessons").upsert({ unit_id: unitId, name: lessonName, display_order: row.sourceRow, ...lessonFields }, { onConflict: "unit_id,name" }).select("id").single();
        if (error) throw databaseFailure(error);
        lessonId = data.id;
        lessons.set(lessonKey, lessonId);
        if (isHeader) await replaceLessonStandardIndex(lessonId, row.contentStandards || "", row.processStandards || "");
      } else if (row.isLessonHeader) {
        const { error } = await supabase.from("curriculum_lessons").update({ instructional_intent: row.learningObjective || "", content_standards: row.contentStandards || "", practice_standards: row.processStandards || "", short_standard: row.shortStandard || "", fundamental_knowledge: row.fundamentalKnowledge || "", applications: row.applications || "", connections: row.connections || "", vocabulary: row.vocabulary || "", notes: row.notes || "", updated_at: new Date().toISOString() }).eq("id", lessonId);
        if (error) throw databaseFailure(error);
        await replaceLessonStandardIndex(lessonId, row.contentStandards || "", row.processStandards || "");
      }
      const { data: importRow, error } = await supabase.from("curriculum_import_rows").insert({ batch_id: batchId, source_row: row.sourceRow, domain_id: domainId, unit_id: unitId, lesson_id: lessonId, lesson: lessonName, learning_objective: row.learningObjective || null, learning_outcome: row.learningOutcome || null, content_standards: row.contentStandards || null, process_standards: row.processStandards || null, short_standard: row.shortStandard || null, fundamental_knowledge: row.fundamentalKnowledge || null, applications: row.applications || null, connections: row.connections || null, vocabulary: row.vocabulary || null, notes: row.notes || null, evidence_test: (row.evidenceLabels || []).join("\n") || null }).select("id").single();
      if (error?.code === "23505") {
        const { data: existingRow, error: existingRowError } = await supabase.from("curriculum_import_rows").select("id, objective_id").eq("batch_id", batchId).eq("source_row", row.sourceRow).maybeSingle();
        if (existingRowError) throw databaseFailure(existingRowError);
        if (existingRow?.objective_id) { onProgress?.(input.rows.indexOf(row) + 1); continue; }
      }
      if (error) throw databaseFailure(error);
      const statement = row.learningOutcome.trim();
      let objectiveId: string | null = null;
      if (statement) {
        objectiveOrder += 1;
        const insertObjective = async () => {
          const { data: objective, error: objectiveError } = await supabase.from("curriculum_objectives").insert({ unit_id: unitId, lesson_id: lessonId, title: statement, statement, learning_objective: "", learning_outcomes: [statement], display_order: objectiveOrder, created_by: user.id }).select("id").single();
          if (objectiveError) throw databaseFailure(objectiveError);
          return objective.id as string;
        };
        const { data: existingObjectives, error: existingError } = await supabase.from("curriculum_objectives").select("id").eq("lesson_id", lessonId).eq("statement", statement).limit(1);
        if (existingError) throw databaseFailure(existingError);
        objectiveId = existingObjectives?.[0]?.id || await insertObjective();
        let { error: linkError } = await supabase.from("curriculum_import_rows").update({ objective_id: objectiveId }).eq("id", importRow.id);
        if (linkError?.code === "23505") {
          objectiveId = await insertObjective();
          ({ error: linkError } = await supabase.from("curriculum_import_rows").update({ objective_id: objectiveId }).eq("id", importRow.id));
        }
        if (linkError) throw databaseFailure(linkError);
        const { error: referenceError } = await supabase.from("curriculum_objective_references").insert({ objective_id: objectiveId, source_title: input.fileName, worksheet_name: input.sheetName, source_row: row.sourceRow });
        if (referenceError) throw databaseFailure(referenceError);
      }
      const evidenceParent = row.isLessonHeader ? { lesson_id: lessonId } : objectiveId ? { objective_id: objectiveId } : null;
      if (evidenceParent) await ensureAssessmentTargets(evidenceParent, row.evidenceLabels || []);
      completed += 1;
      onProgress?.(input.rows.indexOf(row) + 1);
      } catch (error) {
        throw new CurriculumImportError(explainCurriculumImportFailure(error, { phase: "stage", sourceRow: row.sourceRow, lesson: row.lesson, savedCount: completed, expectedCount: input.rows.length }));
      }
    }
    return { batchId };
    } catch (error) {
      if (error instanceof WorkbookAlreadyStagedError || error instanceof CurriculumImportError) throw error;
      throw new CurriculumImportError(explainCurriculumImportFailure(error, { phase: "stage", savedCount: completed, expectedCount: input.rows.length }));
    }
  },
  async listSubjects(): Promise<AdminSubject[]> {
    const { data, error } = await supabase.from("subjects").select("id, name").eq("is_active", true).order("name");
    if (error) throw new Error(error.message);
    return (data || []) as AdminSubject[];
  },
  async listConcepts(subjectId: string): Promise<AdminConcept[]> {
    const { data, error } = await supabase.from("concepts")
      .select("id, subject_id, code, name, description, status")
      .eq("subject_id", subjectId).order("name");
    if (error) throw new Error(error.message);
    return (data || []) as AdminConcept[];
  },
  async listTags(subjectId: string): Promise<AdminTag[]> {
    const { data, error } = await supabase.from("curriculum_tags")
      .select("id, subject_id, code, name").eq("subject_id", subjectId).order("name");
    if (error) throw new Error(error.message);
    return (data || []) as AdminTag[];
  },
  async listConceptTagIds(conceptId: string): Promise<string[]> {
    const { data, error } = await supabase.from("concept_tags").select("tag_id").eq("concept_id", conceptId);
    if (error) throw new Error(error.message);
    return (data || []).map((row) => row.tag_id as string);
  },
  async listObjectives(conceptId: string): Promise<AdminObjective[]> {
    const { data, error } = await supabase.from("learning_objectives")
      .select("id, concept_id, code, outcome, status").eq("concept_id", conceptId).order("code");
    if (error) throw new Error(error.message);
    return (data || []) as AdminObjective[];
  },
  async listSubjectObjectives(subjectId: string): Promise<AdminSubjectObjective[]> {
    const concepts = await this.listConcepts(subjectId);
    if (!concepts.length) return [];
    const { data, error } = await supabase.from("learning_objectives")
      .select("id, concept_id, code, outcome, status")
      .in("concept_id", concepts.map((item) => item.id)).order("code");
    if (error) throw new Error(error.message);
    const conceptsById = new Map(concepts.map((item) => [item.id, item.name]));
    return (data || []).map((item) => ({ ...item, concept_name: conceptsById.get(item.concept_id) || "Unknown concept" })) as AdminSubjectObjective[];
  },
  async listRubrics(objectiveId: string): Promise<AdminRubric[]> {
    const { data, error } = await supabase.from("objective_rubrics")
      .select("id, objective_id, revision, status, success_criteria, evidence_required")
      .eq("objective_id", objectiveId).order("revision", { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []) as AdminRubric[];
  },
  async listPrerequisites(objectiveId: string): Promise<AdminPrerequisite[]> {
    const { data, error } = await supabase.from("objective_prerequisites")
      .select("prerequisite_objective_id, rationale").eq("objective_id", objectiveId);
    if (error) throw new Error(error.message);
    return (data || []) as AdminPrerequisite[];
  },
  async listAuthorities(): Promise<AdminAuthority[]> {
    const { data, error } = await supabase.from("curriculum_authorities")
      .select("id, code, name, country_code, region_code").order("name");
    if (error) throw new Error(error.message);
    return (data || []) as AdminAuthority[];
  },
  async listVersions(authorityId: string): Promise<AdminVersion[]> {
    const { data, error } = await supabase.from("curriculum_versions")
      .select("id, authority_id, version_label, academic_year, source_reference, status")
      .eq("authority_id", authorityId).order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data || []) as AdminVersion[];
  },
  async listPlacements(versionId: string, grade: number): Promise<AdminPlacement[]> {
    const { data, error } = await supabase.from("curriculum_objective_placements")
      .select("id, curriculum_version_id, grade, objective_id, rubric_id, role, requirement, display_order")
      .eq("curriculum_version_id", versionId).eq("grade", grade).order("display_order");
    if (error) throw new Error(error.message);
    return (data || []) as AdminPlacement[];
  },
  async listCurriculumPath(versionId: string, grade: number, subjectId: string): Promise<AdminPathPlacement[]> {
    const placements = await this.listPlacements(versionId, grade);
    if (!placements.length) return [];
    const objectiveIds = [...new Set(placements.map((item) => item.objective_id))];
    const { data: objectiveRows, error: objectiveError } = await supabase.from("learning_objectives")
      .select("id, code, outcome, concept_id").in("id", objectiveIds);
    if (objectiveError) throw new Error(objectiveError.message);
    const conceptIds = [...new Set((objectiveRows || []).map((item) => item.concept_id))];
    if (!conceptIds.length) return [];
    const { data: conceptRows, error: conceptError } = await supabase.from("concepts")
      .select("id, name, subject_id").in("id", conceptIds);
    if (conceptError) throw new Error(conceptError.message);
    const conceptsById = new Map((conceptRows || []).map((item) => [item.id, item]));
    const objectivesById = new Map((objectiveRows || []).filter((item) => conceptsById.get(item.concept_id)?.subject_id === subjectId)
      .map((item) => [item.id, item]));
    const selected = placements.filter((item) => objectivesById.has(item.objective_id))
      .sort((a, b) => a.display_order - b.display_order || a.id.localeCompare(b.id));
    if (!selected.length) return [];
    const { data: edges, error: edgeError } = await supabase.from("objective_prerequisites")
      .select("objective_id, prerequisite_objective_id").in("objective_id", [...objectivesById.keys()]);
    if (edgeError) throw new Error(edgeError.message);
    const firstOrder = new Map<string, number>();
    selected.forEach((item) => {
      if (!firstOrder.has(item.objective_id)) firstOrder.set(item.objective_id, item.display_order);
    });
    return selected.map((item) => {
      const objective = objectivesById.get(item.objective_id)!;
      const prerequisiteIds = (edges || []).filter((edge) => edge.objective_id === item.objective_id)
        .map((edge) => edge.prerequisite_objective_id);
      return {
        ...item,
        objective_code: objective.code,
        outcome: objective.outcome,
        concept_name: conceptsById.get(objective.concept_id)!.name,
        prerequisite_codes: prerequisiteIds.map((id) => objectivesById.get(id)?.code || "Earlier-grade or external objective"),
        order_conflicts: prerequisiteIds.filter((id) => firstOrder.has(id) && firstOrder.get(id)! >= firstOrder.get(item.objective_id)!)
          .map((id) => objectivesById.get(id)?.code || id),
      };
    });
  },
  async reorderCurriculumPath(versionId: string, grade: number, subjectId: string, placementIds: string[]): Promise<void> {
    const { error } = await supabase.rpc("reorder_curriculum_path", {
      p_version_id: versionId, p_grade: grade, p_subject_id: subjectId, p_placement_ids: placementIds,
    });
    if (error) throw new Error(error.message);
  },
  async createConcept(subjectId: string, code: string, name: string): Promise<AdminConcept> {
    const { data, error } = await supabase.from("concepts")
      .insert({ subject_id: subjectId, code: requireCode(code), name: requireText(name, "Concept name") })
      .select("id, subject_id, code, name, description, status").single();
    if (error) throw new Error(error.message);
    return data as AdminConcept;
  },
  async createTag(subjectId: string, code: string, name: string): Promise<AdminTag> {
    const { data, error } = await supabase.from("curriculum_tags")
      .insert({ subject_id: subjectId, code: requireCode(code), name: requireText(name, "Tag name") })
      .select("id, subject_id, code, name").single();
    if (error) throw new Error(error.message);
    return data as AdminTag;
  },
  async attachTag(conceptId: string, subjectId: string, tagId: string): Promise<void> {
    const { error } = await supabase.from("concept_tags")
      .upsert({ concept_id: conceptId, subject_id: subjectId, tag_id: tagId }, { onConflict: "concept_id,tag_id" });
    if (error) throw new Error(error.message);
  },
  async createObjective(conceptId: string, code: string, outcome: string): Promise<AdminObjective> {
    const { data, error } = await supabase.from("learning_objectives")
      .insert({ concept_id: conceptId, code: requireCode(code), outcome: requireText(outcome, "Outcome") })
      .select("id, concept_id, code, outcome, status").single();
    if (error) throw new Error(error.message);
    return data as AdminObjective;
  },
  async importDraftRows(subjectId: string, rows: CurriculumBulkImportRow[], onProgress?: (completed: number) => void): Promise<{ concepts: number; objectives: number; skipped: number }> {
    const existingConcepts = await this.listConcepts(subjectId);
    const conceptsByName = new Map(existingConcepts.map((item) => [item.name.trim().toLocaleLowerCase(), item]));
    let concepts = 0; let objectives = 0; let skipped = 0;
    for (const [index, row] of rows.entries()) {
      const conceptName = requireText(row.concept, "Concept");
      const conceptKey = conceptName.toLocaleLowerCase();
      let concept = conceptsByName.get(conceptKey);
      if (!concept) {
        const codeBase = conceptName.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 42) || "CONCEPT";
        concept = await this.createConcept(subjectId, `${codeBase}_${String(index + 1).padStart(2, "0")}`, conceptName);
        conceptsByName.set(conceptKey, concept); concepts += 1;
      }
      const objectiveText = [row.lesson.trim(), row.learningObjective.trim() || row.learningOutcome.trim()].filter(Boolean).join(": ");
      const existingObjectives = await this.listObjectives(concept.id);
      if (existingObjectives.some((item) => item.outcome.trim().toLocaleLowerCase() === objectiveText.toLocaleLowerCase())) { skipped += 1; onProgress?.(index + 1); continue; }
      const code = `IMP-${String(index + 1).padStart(3, "0")}`;
      const objective = await this.createObjective(concept.id, code, objectiveText);
      const criteria = (row.learningOutcome || row.learningObjective).split(/\n+/).map((item) => item.trim()).filter(Boolean);
      const evidence = [row.contentStandards && `Content standards: ${row.contentStandards}`, row.processStandards && `Practice standards: ${row.processStandards}`, row.applications && `Applications: ${row.applications}`, row.connections && `Connections: ${row.connections}`, row.vocabulary && `Vocabulary: ${row.vocabulary}`].filter(Boolean).join("\n\n");
      if (criteria.length && evidence) await this.createRubric(objective.id, 1, criteria, evidence);
      objectives += 1;
      onProgress?.(index + 1);
    }
    return { concepts, objectives, skipped };
  },
  async createRubric(objectiveId: string, revision: number, criteria: string[], evidenceRequired: string): Promise<AdminRubric> {
    const successCriteria = criteria.map((item) => item.trim()).filter(Boolean);
    if (!successCriteria.length) throw new Error("At least one success criterion is required.");
    if (!Number.isInteger(revision) || revision < 1) throw new Error("Rubric revision must be a positive whole number.");
    const { data, error } = await supabase.from("objective_rubrics")
      .insert({ objective_id: objectiveId, revision, success_criteria: successCriteria, evidence_required: requireText(evidenceRequired, "Required evidence") })
      .select("id, objective_id, revision, status, success_criteria, evidence_required").single();
    if (error) throw new Error(error.message);
    return data as AdminRubric;
  },
  async addPrerequisite(objectiveId: string, prerequisiteObjectiveId: string, rationale: string): Promise<void> {
    const { error } = await supabase.from("objective_prerequisites")
      .insert({ objective_id: objectiveId, prerequisite_objective_id: prerequisiteObjectiveId, rationale: rationale.trim() || null });
    if (error) throw new Error(error.message);
  },
  async removePrerequisite(objectiveId: string, prerequisiteObjectiveId: string): Promise<void> {
    const { error } = await supabase.from("objective_prerequisites")
      .delete().eq("objective_id", objectiveId).eq("prerequisite_objective_id", prerequisiteObjectiveId);
    if (error) throw new Error(error.message);
  },
  async createAuthority(code: string, name: string, countryCode: string, regionCode?: string): Promise<AdminAuthority> {
    const country = countryCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(country)) throw new Error("Use a two-letter country code.");
    const { data, error } = await supabase.from("curriculum_authorities")
      .insert({ code: requireCode(code), name: requireText(name, "Board name"), country_code: country, region_code: regionCode?.trim() || null })
      .select("id, code, name, country_code, region_code").single();
    if (error) throw new Error(error.message);
    return data as AdminAuthority;
  },
  async createVersion(authorityId: string, label: string, academicYear: string, sourceReference: string): Promise<AdminVersion> {
    const versionLabel = requireText(label, "Version label");
    const { data, error } = await supabase.from("curriculum_versions")
      .insert({ authority_id: authorityId, version_label: versionLabel, academic_year: requireText(academicYear, "Academic year"), source_reference: requireText(sourceReference, "Source reference") })
      .select("id, authority_id, version_label, academic_year, source_reference, status").single();
    if (error?.code === "23505") throw new Error(`Version ${versionLabel} already exists for this board. Use a new label, such as 1.1.`);
    if (error) throw new Error(error.message);
    return data as AdminVersion;
  },
  async createPlacement(versionId: string, grade: number, objectiveId: string, rubricId: string, role: AdminPlacement["role"], requirement: AdminPlacement["requirement"], displayOrder: number): Promise<void> {
    if (!Number.isInteger(grade) || grade < 1 || grade > 12) throw new Error("Grade must be between 1 and 12.");
    const { error } = await supabase.from("curriculum_objective_placements")
      .insert({ curriculum_version_id: versionId, grade, objective_id: objectiveId, rubric_id: rubricId, role, requirement, display_order: displayOrder });
    if (error) throw new Error(error.message);
  },
  async saveNodeEvidence(nodeType: "domain" | "unit" | "lesson" | "objective", nodeId: string, evidence: CatalogNodeEvidence): Promise<void> {
    await assertCatalogNodeEditable(nodeType, nodeId);
    const column = parentColumn(nodeType);
    const { data: assessment, error: assessmentError } = await supabase.from("curriculum_evidence_requirements").select("id").eq(column, nodeId).eq("kind", "assessment").maybeSingle();
    if (assessmentError) throw new Error(assessmentError.message);
    let assessmentId = assessment?.id as string | undefined;
    if (!assessmentId && evidence.targets.length) {
      const { data, error } = await supabase.from("curriculum_evidence_requirements").insert({ [column]: nodeId, kind: "assessment" }).select("id").single();
      if (error) throw new Error(error.message);
      assessmentId = data.id;
    }
    if (assessmentId) {
      const { error } = await supabase.from("curriculum_evidence_targets").delete().eq("requirement_id", assessmentId);
      if (error) throw new Error(error.message);
      if (evidence.targets.length) {
        const { error: insertError } = await supabase.from("curriculum_evidence_targets").insert(evidence.targets.map((target, display_order) => ({ requirement_id: assessmentId, target_kind: target.kind, target_id: target.targetId, label: requireText(target.label, "Evidence label"), display_order })));
        if (insertError) throw new Error(insertError.message);
      }
    }
    if (nodeType === "objective") return;
    const { data: feedback, error: feedbackError } = await supabase.from("curriculum_evidence_requirements").select("id").eq(column, nodeId).eq("kind", "node_feedback").maybeSingle();
    if (feedbackError) throw new Error(feedbackError.message);
    if (feedback) {
      const { error } = await supabase.from("curriculum_evidence_requirements").update({ active: evidence.nodeFeedbackActive, prompt: evidence.nodeFeedbackPrompt, updated_at: new Date().toISOString() }).eq("id", feedback.id);
      if (error) throw new Error(error.message);
      return;
    }
    if (!evidence.nodeFeedbackActive && !evidence.nodeFeedbackPrompt.trim()) return;
    const { error } = await supabase.from("curriculum_evidence_requirements").insert({ [column]: nodeId, kind: "node_feedback", active: evidence.nodeFeedbackActive, prompt: evidence.nodeFeedbackPrompt });
    if (error) throw new Error(error.message);
  },
  async publish(table: "concepts" | "learning_objectives" | "objective_rubrics" | "curriculum_versions", id: string): Promise<void> {
    const { data, error } = await supabase.from(table)
      .update({ status: "published", published_at: new Date().toISOString() })
      .eq("id", id).eq("status", "draft").select("id");
    if (error) throw new Error(error.message);
    if (!data?.length) throw new Error("The draft could not be published. Refresh and check its status.");
  },
};
